import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { getAppDomainUrl } from '../../utils/constants';
import {
  WAYGPT_URL,
  PUBLIC_CONTACT_EMAIL,
  resolveDocsPortalUrl,
  shouldRedirectMarketingDocsToPortal,
} from '../../utils/externalLinks';
import axios from '../../utils/axios';
import LogoImage from '../Common/LogoImage';
import { PUBLIC_FOOTER_LOGO_SOURCES } from '../../utils/platformAssets';

/**
 * Подвал публичных страниц.
 * `showChatCta` + `onWriteUs` — кнопка «Написать в чат» для страниц с AI-лаунчером (лендинг).
 * На остальных страницах связаться можно через /contact.
 */
const PublicFooter = ({ showChatCta = false, onWriteUs = null }) => {
  const currentYear = new Date().getFullYear();
  const chatCtaVisible = showChatCta && typeof onWriteUs === 'function';

  const [requisites, setRequisites] = useState(null);

  const loginUrl = getAppDomainUrl('/login');
  const registerUrl = getAppDomainUrl('/register');
  const forgotPasswordUrl = getAppDomainUrl('/forgot-password');
  const docsPortalRedirect = shouldRedirectMarketingDocsToPortal();
  const docsPortalUrl = resolveDocsPortalUrl('/docs');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await axios.get('/api/company-management/public-requisites-public');
        if (cancelled) return;
        if (resp?.data?.success) {
          setRequisites(resp.data.data || {});
        } else {
          setRequisites({});
        }
      } catch (_) {
        if (!cancelled) setRequisites({});
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const orgName = useMemo(() => {
    const v = requisites?.company_name ? String(requisites.company_name).trim() : '';
    return v || 'Маркбэйс';
  }, [requisites?.company_name]);

  const email = useMemo(() => {
    const v = requisites?.email ? String(requisites.email).trim() : '';
    return v || PUBLIC_CONTACT_EMAIL;
  }, [requisites?.email]);

  const phone = useMemo(() => {
    const v = requisites?.phone ? String(requisites.phone).trim() : '';
    return v || '—';
  }, [requisites?.phone]);

  const address = useMemo(() => {
    const v = requisites?.legal_address ? String(requisites.legal_address).trim() : '';
    return v || '—';
  }, [requisites?.legal_address]);

  const websiteHref = useMemo(() => {
    const raw = requisites?.website ? String(requisites.website).trim() : '';
    if (!raw) return '';
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  }, [requisites?.website]);

  const requisitesRows = useMemo(() => ([
    ['ИНН', requisites?.inn],
    ['КПП', requisites?.kpp],
    ['ОГРН', requisites?.ogrn],
    ['Сайт', requisites?.website],
  ]).map(([label, value]) => ({
    label,
    value: value === null || value === undefined ? '' : String(value).trim(),
  })).filter((item) => item.value), [requisites]);

  return (
    <footer className="public-footer">
      <div className="footer-container">
        <div className="public-footer__top-card">
          <div className="public-footer__brand-block">
            <div className="footer-brand">
              <div className="footer-logo-container">
                <LogoImage
                  sources={PUBLIC_FOOTER_LOGO_SOURCES}
                  alt="Маркбэйс"
                  mode="image"
                  height={32}
                  maxWidth={124}
                  style={{ marginRight: 0 }}
                />
              </div>
              <h2 className="public-footer__brand-title">Маркбэйс</h2>
            </div>
            <p className="public-footer__lead">
              Модульная платформа для торговли, складов, заказов, документов, аналитики и AI.
            </p>
            <div className="public-footer__trust">
              <ShieldCheck size={16} aria-hidden />
              <Link to="/trust" className="footer-link">
                Доверие и безопасность
              </Link>
            </div>
          </div>

          <div className="public-footer__cta-card">
            <span className="public-footer__cta-label">Старт без лишнего шума</span>
            <strong>Заведите компанию и подключайте модули по мере роста.</strong>
            <div className="public-footer__cta-actions">
              <a href={registerUrl} className="public-footer__cta-primary">Начать бесплатно</a>
              <a href={loginUrl} className="public-footer__cta-secondary">Войти</a>
              {chatCtaVisible ? (
                <button
                  type="button"
                  className="public-footer__cta-secondary public-footer__cta-chat"
                  onClick={onWriteUs}
                >
                  <MessageCircle size={16} aria-hidden />
                  Написать в чат
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="public-footer__grid public-footer__grid--compact">
          <div className="public-footer__col">
            <h3 className="public-footer__section-title">Платформа</h3>
            <nav className="public-footer__stack" aria-label="Навигация по сайту">
              <Link to="/" className="footer-link">
                Главная
              </Link>
              <Link to="/about" className="footer-link">
                О платформе
              </Link>
              <Link to="/company" className="footer-link">
                Сведения об организации
              </Link>
              <Link to="/pricing" className="footer-link">
                Тарифы
              </Link>
              <a
                href={resolveDocsPortalUrl('/docs/aidock/29-mincifry-reestr-documentation.md')}
                className="footer-link"
                target="_blank"
                rel="noreferrer"
              >
                Документация ПО (реестр)
              </a>
              <Link to="/contact" className="footer-link">
                Контакты
              </Link>
            </nav>
          </div>

          <div className="public-footer__col">
            <h3 className="public-footer__section-title">Модули</h3>
            <nav className="public-footer__stack" aria-label="Продуктовые разделы">
              <Link to="/product/core" className="footer-link">
                Ядро платформы
              </Link>
              <Link to="/product/logistics" className="footer-link">
                Логистика и склад
              </Link>
              <Link to="/product/crm-hrm" className="footer-link">
                CRM и HRM
              </Link>
              <Link to="/product/analytics" className="footer-link">
                Аналитика
              </Link>
              <Link to="/product/security" className="footer-link">
                Безопасность
              </Link>
            </nav>
          </div>

          <div className="public-footer__col">
            <h3 className="public-footer__section-title">AI и входы</h3>
            <nav className="public-footer__stack" aria-label="AI и входы">
              <Link to="/product/waygpt" className="footer-link">
                ВЭЙГПТ
              </Link>
              <Link to="/product/constructor" className="footer-link">
                Конструктор
              </Link>
              <Link to="/product/mplaza" className="footer-link">
                MPLAZA
              </Link>
              <a href={getAppDomainUrl('/')} className="footer-link">
                App Markbase
              </a>
              <a href={WAYGPT_URL} target="_blank" rel="noopener noreferrer" className="footer-link">
                App ВЭЙГПТ
              </a>
            </nav>
          </div>

          <div className="public-footer__col">
            <h3 className="public-footer__section-title">Документы</h3>
            <nav className="public-footer__stack" aria-label="Юридические документы">
              {docsPortalRedirect ? (
                <a href={docsPortalUrl} className="footer-link">
                  Документация
                </a>
              ) : (
                <Link to="/docs" className="footer-link">
                  Документация
                </Link>
              )}
              <Link to="/privacy" className="footer-link">
                Политика конфиденциальности
              </Link>
              <Link to="/terms" className="footer-link">
                Пользовательское соглашение
              </Link>
              <Link to="/personal-data" className="footer-link">
                Обработка персональных данных
              </Link>
              <Link to="/cookies" className="footer-link">
                Файлы cookie
              </Link>
              <Link to="/legal" className="footer-link">
                Все документы
              </Link>
              <a href={forgotPasswordUrl} className="footer-link">
                Восстановить пароль
              </a>
            </nav>
          </div>

          <div className="public-footer__col public-footer__col--wide">
            <h3 className="public-footer__section-title">Контакты</h3>
            <div className="public-footer__stack">
              <p className="public-footer__muted public-footer__org-name">{orgName}</p>
              <p className="public-footer__muted">
                <Mail size={14} aria-hidden className="public-footer__icon-inline" /> {email}
              </p>
              {phone !== '—' ? (
                <p className="public-footer__muted">
                  <Phone size={14} aria-hidden className="public-footer__icon-inline" /> {phone}
                </p>
              ) : null}
              {address !== '—' ? (
                <p className="public-footer__muted">
                  <MapPin size={14} aria-hidden className="public-footer__icon-inline" /> {address}
                </p>
              ) : null}
              {requisitesRows.length ? (
                <dl className="public-footer__requisites">
                  {requisitesRows.map((item) => (
                    <div className="public-footer__requisites-row" key={item.label}>
                      <dt>{item.label}</dt>
                      <dd>
                        {item.label === 'Сайт' && websiteHref ? (
                          <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="public-footer__inline-link">
                            {item.value}
                          </a>
                        ) : item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="public-footer__fineprint">
                  Реквизиты загружаются из публичного реестра оператора платформы.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="public-footer__divider-major" role="separator" />

        <div className="public-footer__row-bottom">
          <div className="public-footer__legal">
            <p className="public-footer__copyright">
              © {currentYear} {orgName}. Все права защищены.
            </p>
            <p className="public-footer__fineprint">
              Платформа{' '}
              <a
                href="https://markbase.ru"
                target="_blank"
                rel="noopener noreferrer"
                className="public-footer__inline-link public-footer__studio-link"
              >
                <img src="/brand/ecosystem/markbase-mark.svg" alt="" width={20} height={20} className="public-footer__eco-mark" />
                markbase.ru
              </a>
              {' · '}
              разработка{' '}
              <a
                href="https://w7c.ru"
                target="_blank"
                rel="noopener noreferrer"
                className="public-footer__inline-link public-footer__studio-link"
              >
                <img src="/brand/ecosystem/w7c-mark.svg" alt="" width={20} height={20} className="public-footer__eco-mark" />
                w7c.ru
              </a>
              . Защита от DDOS. Шифрование данных.
            </p>
          </div>
          <nav className="public-footer__social-row" aria-label="Быстрые ссылки">
            <Link to="/pricing" className="footer-link public-footer__social-row-link">
              Тарифы
            </Link>
            {docsPortalRedirect ? (
              <a href={docsPortalUrl} className="footer-link public-footer__social-row-link">
                Документация
              </a>
            ) : (
              <Link to="/docs" className="footer-link public-footer__social-row-link">
                Документация
              </Link>
            )}
            <Link to="/contact" className="footer-link public-footer__social-row-link">
              Контакты
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
